import { useMemo, useState, type FormEvent } from 'react';
import { Button, Field, Input, Select } from '@biovet/ui';
import { ApiError, apiSend, useResource } from '../lib/api.js';

/**
 * Sign in.
 *
 * Centred rather than split: brand and one line of book context above, the card
 * in the middle, a greeting below. Nothing on this page describes what the
 * business does — a sign-in prompt is not a place to explain a company to
 * somebody who already works there.
 *
 * **There is no auth backend yet.** `apps/api` attributes every write to the
 * `?email=` in the query string; this screen chooses that email and the password
 * is captured but not verified. `onSignIn` is the one line that changes when the
 * real endpoint lands.
 *
 * Single sign-on (Google, Microsoft) is not here. It needs an OAuth client ID
 * and secret registered by whoever owns the workspace, plus a server that can
 * hold a session — neither of which exists yet. The second-factor field went
 * with it: a code box that verifies nothing teaches people to ignore a code box.
 */
/** Who signed in here last, so the greeting can use their name. */
const LAST_USER_KEY = 'biovet.lastUserId';

interface ClientInfo {
  ip: string | null;
  city: string | null;
}

interface CompanyOption {
  code: string;
  name: string;
  legalName: string;
}

/**
 * `onSignIn` takes nothing.
 *
 * It used to take the email the API returned, and `App` stored it as the
 * session. What comes back from `POST /api/login` now is a `Set-Cookie` the
 * browser handles and this code cannot read — so the only thing left to report
 * upwards is *that* it worked, and who the user is comes from
 * `GET /api/session` on the next render.
 */
export function LoginScreen({ onSignIn }: { onSignIn: () => void }) {
  // Read once, at mount: a localStorage hit on every keystroke buys nothing.
  const [remembered] = useState(readLastUserId);
  const [userId, setUserId] = useState(remembered ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<{ field: 'user' | 'password'; message: string } | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  const clientRes = useResource<ClientInfo>('/client-info');
  const companiesRes = useResource<{ companies: CompanyOption[] }>('/companies');
  const companies = companiesRes.data?.companies ?? [];

  /**
   * The greeting is chosen once per mount, not per render.
   *
   * Re-rolling the wording on every keystroke would make the page flicker while
   * somebody types their password.
   */
  const greeting = useMemo(() => pickGreeting(karachiHour(), nameFor(remembered)), [remembered]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const typed = userId.trim();

    if (!typed) {
      setError({ field: 'user', message: 'Enter your user ID.' });
      return;
    }
    if (!password) {
      setError({ field: 'password', message: 'Enter your password.' });
      return;
    }

    setError(null);
    setSigningIn(true);
    try {
      // The password is checked in PostgreSQL against a bcrypt hash. A wrong
      // user ID, a wrong password and a deactivated account come back
      // identically, so the message below cannot say which one it was —
      // deliberately. Too many failures in the window come back as a 429, whose
      // message is passed straight through.
      //
      // The response body names the user; the *session* is the `Set-Cookie` the
      // browser stored and this code never sees.
      await apiSend<{ email: string; fullName: string }>('POST', '/login', {
        userId: typed,
        password,
      });
      rememberUserId(typed.toLowerCase());
      onSignIn();
    } catch (cause) {
      setSigningIn(false);
      setError({
        field: 'password',
        message:
          cause instanceof ApiError
            ? cause.message
            : 'Cannot reach the API. Start it with `pnpm api`.',
      });
    }
  };

  return (
    <div className="min-h-screen overflow-y-auto bg-[var(--bg)] px-5 py-12">
      <div className="mx-auto flex w-full max-w-[400px] flex-col items-center">
        <header className="bb-rise-in flex flex-col items-center text-center">
          <div className="flex items-center gap-2.5">
            <div className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-[var(--accent-solid)] text-[15px] font-bold tracking-tight text-[var(--accent-ink)]">
              b
            </div>
            <div className="flex flex-col items-start leading-none">
              <span className="text-[16px] font-semibold tracking-tight">biovet</span>
              <span className="bb-eyebrow">Accounting</span>
            </div>
          </div>

          <p className="mt-4 mb-0 text-[12.5px] text-[var(--ink-2)]">
            Double-entry books · PKR · financial year 2026
          </p>
        </header>

        <form
          onSubmit={(e) => void submit(e)}
          className="bb-rise-in mt-7 w-full rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-7 shadow-[var(--shadow)]"
          style={{ animationDelay: '0.08s' }}
        >
          <h2 className="m-0 text-[22px] font-semibold tracking-tight">Sign in</h2>
          <p className="bb-num mt-2 mb-0 text-[12.5px] text-[var(--ink-2)]">
            {describeConnection(clientRes.data, clientRes.loading)}
          </p>

          <div className="flex flex-col gap-3.5 pt-6">
            <Field label="Company" htmlFor="login-company">
              <Select id="login-company">
                {companies.length === 0 ? (
                  <option>{companiesRes.loading ? 'Loading…' : 'Biovet (Pvt.) Limited'}</option>
                ) : (
                  companies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.legalName}
                    </option>
                  ))
                )}
              </Select>
            </Field>

            <Field
              label="User ID"
              htmlFor="login-user"
              error={error?.field === 'user' ? error.message : undefined}
            >
              {/* No datalist of user IDs. This page is reachable by anyone, and
                  a dropdown of every account in the book is an enumeration list
                  handed out before anybody has proved who they are. */}
              <Input
                id="login-user"
                autoComplete="username"
                autoFocus
                value={userId}
                invalid={error?.field === 'user'}
                onChange={(e) => setUserId(e.target.value)}
              />
            </Field>

            {/*
              The hint is deliberate. No password is stored — every seeded user
              carries the literal placeholder hash `PLACEHOLDER_REPLACE_ON_FIRST_LOGIN`
              and nothing verifies it — so somebody looking for a password would
              be looking for something that does not exist. Delete this line in
              the same commit that makes the field real.
            */}
            <Field
              label="Password"
              htmlFor="login-password"
              error={error?.field === 'password' ? error.message : undefined}
            >
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                invalid={error?.field === 'password'}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>

            <div className="flex justify-end pt-0.5">
              <a href="#forgot" className="text-[13px] text-[var(--accent)] hover:underline">
                Forgot password
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={signingIn}
              className="mt-1.5"
            >
              {signingIn ? 'Opening your books…' : 'Sign in'}
            </Button>
          </div>

          <div className="flex items-start gap-2.5 pt-5 text-[12px] leading-relaxed text-[var(--ink-3)] text-pretty">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              className="mt-0.5 shrink-0"
              aria-hidden="true"
            >
              <path d="M12 3l7 4v6c0 4-3 6.5-7 8-4-1.5-7-4-7-8V7z" />
            </svg>
            <span>
              Your password is never stored and nobody here can read it. Everything you enter is
              saved under your name, and an entry once posted can be corrected but never quietly
              changed.
            </span>
          </div>
        </form>

        <p
          className="bb-rise-in mt-7 max-w-[38ch] text-center text-[13.5px] leading-relaxed text-[var(--ink-2)] text-pretty"
          style={{ animationDelay: '0.16s' }}
        >
          {greeting}
        </p>
      </div>
    </div>
  );
}

// ── Connection line ──────────────────────────────────────────────────────────

/**
 * `203.0.113.4 · Karachi, Sindh` — where this browser is connecting from.
 *
 * The city half is present only when the deployment has configured a lookup
 * (`GEOIP_URL` on the API); otherwise the address stands alone, which is
 * already enough to notice a session that is not yours.
 */
function describeConnection(info: ClientInfo | undefined, loading: boolean): string {
  if (loading) return 'Checking your connection…';
  if (!info?.ip) return 'Signing in from this device';
  // A loopback address is the developer's own machine. Printing `::1` at
  // somebody tells them nothing they did not already know.
  if (info.ip === '::1' || info.ip === '127.0.0.1') return 'This device · local network';
  return info.city ? `${info.ip} · ${info.city}` : info.ip;
}

// ── Greeting ─────────────────────────────────────────────────────────────────

type Band = 'morning' | 'afternoon' | 'evening' | 'night';

/**
 * The hour in the book's timezone, not the browser's.
 *
 * Every timestamp in this application renders as Asia/Karachi; a greeting that
 * said "good evening" to somebody whose books say it is lunchtime would be the
 * only thing on screen disagreeing with the ledger.
 */
function karachiHour(): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    hour12: false,
    timeZone: 'Asia/Karachi',
  }).format(new Date());
  // '24' at midnight in some engines; the band table treats 0 and 24 alike.
  return Number(hour) % 24;
}

function bandFor(hour: number): Band {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'night';
}

/**
 * Four bands, three phrasings each, one picked at random.
 *
 * `{name}` becomes ", Ayesha" when this device has signed somebody in before and
 * drops to nothing when it has not — so a first sign-in reads as a sentence
 * rather than as a form with a blank in it.
 */
const GREETINGS: Record<Band, string[]> = {
  morning: [
    'Good morning{name}. The books are where you left them.',
    'Morning{name} — yesterday is closed, today is open.',
    'Good morning{name}. A quiet ledger is a good way to start.',
  ],
  afternoon: [
    'Good afternoon{name}. Half the day is still postable.',
    'Afternoon{name} — plenty of period left.',
    'Good afternoon{name}. Back for the second half.',
  ],
  evening: [
    'Good evening{name}. Worth clearing the queue before you go.',
    'Evening{name} — a good hour for a trial balance.',
    'Good evening{name}. Last entries of the day.',
  ],
  night: [
    'Working late{name}? The ledger keeps your hours.',
    'Late night{name}. Whatever you post will still be here tomorrow.',
    'It is late{name} — post carefully, or leave it as a draft.',
  ],
};

function pickGreeting(hour: number, name: string | null): string {
  const options = GREETINGS[bandFor(hour)];
  const line = options[Math.floor(Math.random() * options.length)] ?? options[0]!;
  return line.replace('{name}', name ? `, ${name}` : '');
}

/**
 * `ayesha.raza` → `Ayesha`, `junaid` → `Junaid`.
 *
 * Derived from the user ID rather than looked up, because the greeting renders
 * before anyone has signed in and this page must not ask the API who exists.
 */
function nameFor(userId: string | null): string | null {
  // The longest segment, not the first: `h.siddiqui` is Siddiqui, not H. Below
  // three letters nothing in the ID is a name worth greeting, so the sentence
  // does without one.
  const best = (userId ?? '')
    .split(/[.\-_@]/)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)[0];
  if (!best || best.length < 3) return null;
  return best.charAt(0).toUpperCase() + best.slice(1);
}

function readLastUserId(): string | null {
  try {
    return window.localStorage.getItem(LAST_USER_KEY);
  } catch {
    // Private browsing, or storage disabled by policy. A greeting is not worth
    // an exception.
    return null;
  }
}

function rememberUserId(userId: string): void {
  try {
    window.localStorage.setItem(LAST_USER_KEY, userId);
  } catch {
    /* ignore */
  }
}
